import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { ExclamationCircleIcon, MailIcon } from '@heroicons/react/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/solid';
import { GiftChoiceUnitData } from '@shared/api/giftChoiceUnit/giftChoiceUnit.interface';
import {
  giftCouponByCodeDataAtomFamily,
  giftCouponByCodeStatusAtomFamily,
} from '@shared/api/giftCoupon/giftCoupon.atom';
import {
  requestCouponRedemption,
  viewCoupon,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import { useGiftCouponByCodeHook } from '@shared/api/giftCoupon/giftCoupon.hook';
import { GiftCouponRequestDto } from '@shared/api/giftCoupon/giftCoupon.interface';
import { Loading } from '@shared/components/Loading';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { useParams, useSearchParams } from 'react-router-dom';

import { giftStoreNavBarTitleAtom } from '../containers/GiftStoreNavBar/giftStoreNavBar.atom';
import CouponStatusView from './giftCouponRedeem/CouponStatusView';
import {
  getNextCouponSendSlot,
  getRedeemStatusMessage,
} from './giftCouponRedeem/giftCouponRedeem.utils';
import PhoneVerificationSection from './giftCouponRedeem/PhoneVerificationSection';
import ProductImageCarousel, {
  CarouselImage,
} from './giftCouponRedeem/ProductImageCarousel';
import SignatureModal from './giftCouponRedeem/SignatureModal';
import SubmitSuccessView from './giftCouponRedeem/SubmitSuccessView';

const GiftCouponRedeemPage: FC = function GiftCouponRedeemPage() {
  const { couponCode } = useParams<{ couponCode: string }>();
  const [searchParams] = useSearchParams();
  const isAdminPreview =
    searchParams.get('secretKey') === process.env.REACT_APP_GIFT_STORE_ADMIN_PREVIEW_KEY;

  const setTitle = useSetRecoilState(giftStoreNavBarTitleAtom);

  // 폼 상태
  const [phoneNumber, setPhoneNumber] = useState('');
  const [selectedChoiceUnit, setSelectedChoiceUnit] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [redeemSubmitMeta, setRedeemSubmitMeta] = useState<{
    submittedAt: Date;
    expectedSendAt: Date;
  } | null>(null);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);

  // 쿠폰 조회
  useGiftCouponByCodeHook(couponCode ?? '');
  const status = useRecoilValue(giftCouponByCodeStatusAtomFamily(couponCode ?? ''));
  const coupon = useRecoilValue(giftCouponByCodeDataAtomFamily(couponCode ?? ''));

  useEffect(() => {
    setTitle('답례품 수령');
  }, [setTitle]);

  useEffect(() => {
    if (coupon && !isAdminPreview) {
      viewCoupon(coupon._id).catch(console.error);
    }
  }, [coupon, isAdminPreview]);

  // 상품 정보
  const giftItem = coupon?.giftItem;
  const giftItemInfo = typeof giftItem === 'object' ? giftItem?.giftItemInfo : null;

  const isSelectable = giftItemInfo?.isSelectable ?? false;
  const choiceUnits = useMemo(() => {
    return (giftItemInfo?.choiceUnits ?? []) as GiftChoiceUnitData[];
  }, [giftItemInfo]);

  const selectedUnit = useMemo(() => {
    if (!selectedChoiceUnit) return null;
    return choiceUnits.find((u) => u._id === selectedChoiceUnit) ?? null;
  }, [selectedChoiceUnit, choiceUnits]);

  const imageUrl = useMemo(() => {
    if (selectedUnit?.image?.url) return selectedUnit.image.url;
    if (typeof giftItem === 'object') {
      return giftItemInfo?.image?.url || giftItem?.image?.url || '';
    }
    return '';
  }, [giftItem, giftItemInfo, selectedUnit]);

  const brandName = useMemo(() => {
    if (selectedUnit?.giftItemBrand && typeof selectedUnit.giftItemBrand === 'object') {
      return selectedUnit.giftItemBrand.name;
    }
    if (giftItemInfo) {
      const brand = giftItemInfo.giftItemBrand;
      if (brand && typeof brand === 'object' && 'name' in brand) {
        return brand.name;
      }
    }
    return null;
  }, [giftItemInfo, selectedUnit]);

  const itemName =
    typeof giftItem === 'object' ? giftItem?.name || giftItemInfo?.name : '';

  const couponDetail = useMemo(() => {
    if (selectedUnit?.couponDetail) return selectedUnit.couponDetail;
    const brandCouponDetail =
      selectedUnit?.giftItemBrand && typeof selectedUnit.giftItemBrand === 'object'
        ? selectedUnit.giftItemBrand.couponDetail
        : undefined;
    return brandCouponDetail ?? giftItemInfo?.couponDetail;
  }, [giftItemInfo, selectedUnit]);

  const phoneInputRef = useRef<HTMLDivElement>(null);

  // 캐로셀
  const [carouselIndex, setCarouselIndex] = useState(0);

  const mainImageUrl = useMemo(() => {
    if (typeof giftItem === 'object') {
      return giftItemInfo?.image?.url || giftItem?.image?.url || '';
    }
    return '';
  }, [giftItem, giftItemInfo]);

  const carouselImages: CarouselImage[] = useMemo(() => {
    if (!isSelectable || choiceUnits.length === 0) {
      return [{ url: mainImageUrl, name: itemName || '답례품', unitId: null, brandName }];
    }
    const mainSlide: CarouselImage = {
      url: mainImageUrl,
      name: itemName || '답례품',
      unitId: null,
      brandName,
    };
    const unitSlides: CarouselImage[] = choiceUnits.map((unit) => ({
      url: unit.image?.url || '',
      name: unit.name,
      unitId: unit._id,
      brandName:
        unit.giftItemBrand && typeof unit.giftItemBrand === 'object'
          ? unit.giftItemBrand.name
          : null,
    }));
    return [mainSlide, ...unitSlides];
  }, [isSelectable, choiceUnits, mainImageUrl, itemName, brandName]);

  useEffect(() => {
    if (!isSelectable) return;
    const unitId = carouselImages[carouselIndex]?.unitId ?? null;
    setSelectedChoiceUnit(unitId);
  }, [carouselIndex, isSelectable, carouselImages]);

  const handleSelectUnit = useCallback(
    (unitId: string | null) => {
      setSelectedChoiceUnit(unitId);
      if (unitId === null) {
        setCarouselIndex(0);
      } else {
        const idx = carouselImages.findIndex((item) => item.unitId === unitId);
        if (idx >= 0) setCarouselIndex(idx);
        setTimeout(() => {
          phoneInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 200);
      }
    },
    [carouselImages],
  );

  // 서명 모달 열기 (유효성 검사 후)
  const handleOpenSignature = useCallback(() => {
    const cleanPhone = phoneNumber.replace(/-/g, '');

    if (!coupon || !cleanPhone) {
      setSubmitError('휴대폰 번호를 입력해 주세요.');
      return;
    }

    if (!isVerified) {
      setSubmitError('번호 인증을 먼저 완료해 주세요.');
      return;
    }

    if (isSelectable && !selectedChoiceUnit) {
      setSubmitError('교환할 상품을 선택해 주세요.');
      return;
    }

    setSubmitError(null);
    setIsSignatureModalOpen(true);
  }, [coupon, phoneNumber, isVerified, isSelectable, selectedChoiceUnit]);

  // 서명 완료 후 실제 교환 신청
  const handleSubmitWithSignature = useCallback(
    async (signatureDataUrl: string) => {
      const cleanPhone = phoneNumber.replace(/-/g, '');
      if (!coupon) return;

      setIsSubmitting(true);
      setSubmitError(null);

      try {
        const dto: GiftCouponRequestDto = {
          name: '',
          phoneNumber: cleanPhone,
          signatureImage: signatureDataUrl,
        };
        if (selectedChoiceUnit) dto.selectedChoiceUnit = selectedChoiceUnit;

        await requestCouponRedemption(coupon._id, dto);
        const submittedAt = new Date();
        setRedeemSubmitMeta({
          submittedAt,
          expectedSendAt: getNextCouponSendSlot(submittedAt),
        });
        setIsSignatureModalOpen(false);
        setSubmitSuccess(true);
      } catch (error) {
        console.error('교환 신청 실패:', error);
        setSubmitError('교환 신청에 실패했습니다. 다시 시도해 주세요.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [coupon, phoneNumber, selectedChoiceUnit],
  );

  // --- 렌더링 분기 ---

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="center-box min-h-screen">
        <Loading />
      </div>
    );
  }

  if (!coupon) {
    return (
      <div className="center-box min-h-screen flex-col gap-4 px-4 text-center">
        <ExclamationCircleIcon className="h-16 w-16 text-gray-300" />
        <p className="text-gray-500">쿠폰을 찾을 수 없습니다.</p>
        <p className="text-sm text-gray-400">
          유효하지 않은 쿠폰 코드이거나 이미 사용된 쿠폰입니다.
        </p>
      </div>
    );
  }

  const statusMessage = getRedeemStatusMessage(coupon.redeemStatus);

  // 신청 직후 성공 화면
  if (submitSuccess) {
    const submitSelectedUnit = selectedChoiceUnit
      ? choiceUnits.find((u) => u._id === selectedChoiceUnit) ?? null
      : null;

    return (
      <SubmitSuccessView
        redeemSubmitMeta={redeemSubmitMeta}
        imageUrl={imageUrl}
        itemName={itemName || ''}
        couponCode={couponCode || ''}
        brandName={brandName}
        selectedUnit={submitSelectedUnit}
      />
    );
  }

  // 이미 처리된 쿠폰
  if (statusMessage) {
    return (
      <CouponStatusView
        coupon={coupon}
        statusMessage={statusMessage}
        imageUrl={imageUrl}
        itemName={itemName || ''}
        couponCode={couponCode || ''}
        brandName={brandName}
      />
    );
  }

  const isMainSlide = carouselIndex === 0;
  const displayBrandName = isMainSlide ? brandName : null;

  // 교환 신청 폼
  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-100 to-white pb-8">
      <ProductImageCarousel
        images={carouselImages}
        carouselIndex={carouselIndex}
        onIndexChange={setCarouselIndex}
        displayBrandName={displayBrandName}
      />

      <div className="px-4 pt-5">
        {/* 상품명 */}
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-base font-medium text-stone-800">
            {selectedUnit?.name || itemName}
          </h2>
          <span className="flex-shrink-0 text-xs text-stone-500">{couponCode}</span>
        </div>

        {/* 유효기간 안내 */}
        <div className="mt-4 space-y-2">
          {coupon.expiresAt && (
            <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
              <div>
                <span className="text-sm font-medium text-amber-800">신청 기한</span>
                <p className="text-xs text-amber-600">이 링크로 신청 가능한 기한</p>
              </div>
              <span className="text-sm font-bold text-amber-800">
                ~
                {new Date(coupon.expiresAt).toLocaleDateString('ko-KR', {
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                })}
              </span>
            </div>
          )}

          {couponDetail && (couponDetail.validPeriod || couponDetail.placeOfExchange) && (
            <div className="rounded-lg bg-stone-50 p-3">
              {couponDetail.validPeriod && (
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-bold text-stone-600">
                      상품 유효기간
                    </span>
                    <p className="text-xs font-medium text-stone-400">
                      발송된 쿠폰의 사용 기한
                    </p>
                  </div>
                  <span className="text-sm font-medium text-stone-700">
                    {couponDetail.validPeriod}
                  </span>
                </div>
              )}
              {couponDetail.placeOfExchange && (
                <div
                  className={`flex items-center justify-between gap-4 ${
                    couponDetail.validPeriod ? 'mt-2 border-t border-stone-200 pt-2' : ''
                  }`}
                >
                  <span className="whitespace-pre text-sm font-bold text-stone-500">
                    사용처
                  </span>
                  <span className="break-keep text-sm text-stone-600">
                    {couponDetail.placeOfExchange}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* 선택형 상품 리스트 */}
        {isSelectable && choiceUnits.length > 0 && (
          <div className="mt-6">
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-stone-800 px-2.5 py-0.5 text-base font-bold text-white">
                STEP 1
              </span>
              <h3 className="break-keep text-lg font-bold text-stone-700">
                원하시는 상품을 선택해 주세요
              </h3>
            </div>

            <div className="mt-3 space-y-2">
              {choiceUnits.map((unit) => (
                <button
                  key={unit._id}
                  onClick={() => handleSelectUnit(unit._id)}
                  className={`flex w-full items-center gap-3 rounded-lg border p-3 transition ${
                    selectedChoiceUnit === unit._id
                      ? 'border-stone-700 bg-stone-100'
                      : 'border-stone-200 bg-white'
                  }`}
                >
                  {unit.image?.url && (
                    <img
                      src={fileBucketUrl(unit.image.url, 'w200')}
                      alt={unit.name}
                      className="h-14 w-14 rounded object-cover"
                    />
                  )}
                  <div className="flex-1 text-left">
                    {unit.giftItemBrand?.name && (
                      <span className="mb-0.5 inline-block rounded-full border border-gray-400 bg-white px-2 py-0.5 text-xs font-medium text-stone-600">
                        {unit.giftItemBrand?.name}
                      </span>
                    )}
                    <p
                      className={`break-keep font-medium ${
                        selectedChoiceUnit === unit._id
                          ? 'text-stone-900'
                          : 'text-stone-700'
                      }`}
                    >
                      {unit.name}
                    </p>
                  </div>
                  {selectedChoiceUnit === unit._id && (
                    <CheckCircleSolidIcon className="h-7 w-7 text-stone-800" />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 핸드폰 번호 입력 + 인증 */}
        <div ref={phoneInputRef}>
          <PhoneVerificationSection
            couponId={coupon._id}
            isSelectable={isSelectable}
            phoneNumber={phoneNumber}
            onPhoneNumberChange={setPhoneNumber}
            isVerified={isVerified}
            onVerified={() => setIsVerified(true)}
            onVerificationReset={() => setIsVerified(false)}
            submitError={submitError}
            isSubmitting={isSubmitting}
            onSubmit={handleOpenSignature}
          />
        </div>

        {/* 유의사항 */}
        <div className="mt-4 rounded-lg bg-stone-100 p-3">
          <p className="text-xs font-medium text-stone-500">유의사항</p>
          <ul className="mt-1.5 space-y-1 text-xs text-stone-400">
            <li>
              • 교환권 신청 기한 및 상품 유효기간 내 미사용으로 인한 소멸은 본인에게
              책임이 있습니다.
            </li>
            <li>• 발송된 쿠폰은 해당 브랜드의 정책에 따라 사용 가능합니다.</li>
            <li>• 잘못된 번호 입력으로 인한 발송 오류는 재발송이 불가합니다.</li>
          </ul>

          <p className="mt-3 text-xs font-medium text-stone-500">개인정보 처리 안내</p>
          <ul className="mt-1.5 space-y-1 text-xs text-stone-400">
            <li>• 수집하는 개인정보: 수령인 휴대폰 번호</li>
            <li>• 수집 및 이용 목적: 답례품 전달 및 중복 발송 확인·관리</li>
            <li>
              • 위 목적 외에 개인정보를 일체 사용하지 않으며, 답례품 발송 완료 후 관련
              법령에 따라 보관 후 파기합니다.
            </li>
          </ul>
        </div>

        {/* 이용문의 */}
        <div className="mt-4 text-center">
          <a
            href="mailto:smartnanumi@kakao.com"
            className="inline-flex items-center gap-1 text-xs text-stone-300 transition hover:text-stone-400"
          >
            <MailIcon className="h-3 w-3" />
            <span>문의 smartnanumi@kakao.com</span>
          </a>
        </div>
      </div>

      <SignatureModal
        open={isSignatureModalOpen}
        onClose={() => setIsSignatureModalOpen(false)}
        onConfirm={handleSubmitWithSignature}
        isSubmitting={isSubmitting}
      />
    </div>
  );
};

export default GiftCouponRedeemPage;
